//
//  projects.ts
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: October 22, 2026
//  Author: Bob Keathley
//
//  Types and helpers for Projects (spec §6s) and per-project releases (§6t):
//  the wire shapes the Projects pages read, the role gates, and a line diff
//  for comparing a refined agent against its scanned version.
//
import { atLeast } from '../auth'
import type { Page } from './types'

export type ProjectSummary = {
  id: string
  name: string
  folder_name: string
  description: string | null
  repo_count: number
  agent_count: number
  drift_count: number
  top_level_agent: string | null
  last_snapshot_at: number | null
  release_count: number
  created_at: number
}

/** §6s scopes: where on the machine Claude Code loaded an item from. `depth` is set only for ancestors (1 = immediate parent). */
export type Scope = 'project' | 'ancestor' | 'user' | 'managed'
export type Scoped = { scope: Scope; depth: number | null }
export type ShadowedRef = { scope: Scope; depth: number | null; path_rel: string }

export function scopeLabel(s: Scoped): string {
  if (s.scope === 'project') return 'This project'
  if (s.scope === 'ancestor') return `Parent folder (${s.depth ?? '?'} up)`
  if (s.scope === 'user') return 'Your user folder'
  return 'Managed policy'
}
const scopeRank = (s: Scoped) => (s.scope === 'project' ? 0 : s.scope === 'ancestor' ? 1 : s.scope === 'user' ? 2 : 3)

/** Groups items by scope in the order Claude Code prefers them: project, ancestors nearest first, user, managed. */
export function groupByScope<T extends Scoped>(items: T[]): { key: string; label: string; items: T[] }[] {
  const groups = new Map<string, { key: string; label: string; rank: number; depth: number; items: T[] }>()
  for (const it of items) {
    const key = `${it.scope}:${it.depth ?? ''}`
    const g = groups.get(key) ?? { key, label: scopeLabel(it), rank: scopeRank(it), depth: it.depth ?? 0, items: [] }
    g.items.push(it)
    groups.set(key, g)
  }
  return [...groups.values()].sort((a, b) => a.rank - b.rank || a.depth - b.depth)
}

export type ProjectRepo = { root_rel: string; remote: string; branch: string; worktrees: { name: string; branch: string; location: 'inside' | 'outside' }[] }
export type ProjectRule = Scoped & { paths_removed?: number; path_rel: string; kind: 'claude_md' | 'rule'; body: string; sha256: string }
export type ProjectSkill = Scoped & { paths_removed?: number; name: string; path_rel: string; frontmatter: Record<string, unknown>; body: string; sha256: string }
export type PermissionPatterns = { allow?: string[]; deny?: string[]; ask?: string[] }
export type ProjectSettings = Scoped & { paths_removed?: number; permission_patterns?: PermissionPatterns; path_rel: string; hook_events: string[]; permissions: { allow: number; deny: number }; mcp_servers: string[] }

export type AgentVersionRef = Scoped & { path_rel: string; paths_removed: number; version_id: string; sha256: string; source: 'scan' | 'tenant'; frontmatter: Record<string, unknown>; body: string; created_at: number; created_by: string | null }
/** What a birth-all Notebook holds (§6s *Birth source `import`*): tools from the agent file, forbids from the settings deny patterns. `tool_allowlist: null` = no allowlist (all tools). */
export type NotebookRules = { tool_allowlist: string[] | null; forbid_bash: string[]; forbid_tool: string[] }

/** Where a forbid came from: the first settings item (spec order not needed for display) whose deny list holds it. `Bash(p)` entries become `forbid_bash: p`; the rest are the whole pattern. */
export function forbidSource(settings: ProjectSettings[], kind: 'bash' | 'tool', rule: string): string | null {
  const pat = kind === 'bash' ? `Bash(${rule})` : rule
  const hit = settings.find((s) => s.permission_patterns?.deny?.includes(pat))
  return hit ? `${scopeLabel(hit)} · ${hit.path_rel}` : null
}

/** What a project Notebook was born from, for display: imported, a template, or intake answers. */
export function notebookSourceLabel(n: { source?: string; template: { namespace: string; name: string; version: string } | null }): string {
  if (n.source === 'import') return 'imported'
  if (n.template) return `${n.template.namespace}/${n.template.name}@${n.template.version}`
  return 'intake answers'
}

export type ProjectAgent = Scoped & {
  name: string
  /** Same-name definitions in lower-priority scopes; they ship nowhere. */
  shadowed: ShadowedRef[]
  purpose: string | null
  current: AgentVersionRef
  latest_scan: { version_id: string; sha256: string; snapshot_id: string; created_at: number } | null
  drift: boolean
  notebook: { notebook_id: string; birth_cert_id: string; /** `'import'` for a Notebook born from the imported agent and settings (birth-all). */ source?: 'template' | 'answers' | 'import'
    /** null for a Notebook born without a template. */ template: { namespace: string; name: string; version: string } | null; compiled_hash: string } & Partial<NotebookRules> | null
  top_level: boolean
}

export type ProjectDetail = {
  id: string
  name: string
  folder_name: string
  description: string | null
  top_level_agent: string | null
  created_at: number
  created_by: string | null
  latest_snapshot: { id: string; scanned_at: number; client_build: string; created_at: number; size: number } | null
  repos: ProjectRepo[]
  rules: ProjectRule[]
  skills: ProjectSkill[]
  settings: ProjectSettings[]
  agents: ProjectAgent[]
  linked_machines: { machine_id: string; machine_user_id: string; linked_at: number }[]
}

export type SnapshotMeta = {
  id: string
  scanned_at: number
  client_build: string
  size: number
  sha256: string
  source: 'machine' | 'import'
  machine_id: string | null
  created_by: string | null
  created_at: number
  agent_count: number
  agents_changed_count: number
}

export type AgentVersion = Scoped & { path_rel: string; paths_removed: number;
  version_id: string
  sha256: string
  source: 'scan' | 'tenant'
  frontmatter: Record<string, unknown>
  body: string
  snapshot_id: string | null
  created_by: string | null
  created_at: number
  current: boolean
}

export type ProjectReleaseMeta = {
  id: string
  seq: number
  release_id: string
  issued_by: string
  issued_at: number
  key_id: string
  notebook_count: number
  agent_count: number
  top_level_agent: string | null
  hash: string
}

/** `POST /v1/projects/{id}/notebooks/birth-all`: one signed Notebook for each agent that has none. */
export type BirthAllResult = {
  born: ({ agent: string; notebook_id: string; birth_cert_id: string; compiled_hash: string } & NotebookRules)[]
  skipped: { agent: string; reason: string }[]
}

/** The canon block both answers routes require (§6s SIG-167): nine axis answers, a sector and an accountability line. */
export type AnswersCanon = { sector: string; answers: string[]; accountability: string }
/** Body of `POST /v1/projects/{id}/agents/{name}/answers/preview` and `…/rebirth`; `answers` holds only the questions the person answered. */
export type AnswersRequest = { answers: Record<string, unknown>; canon?: AnswersCanon | null }

export type AnswerLineage = { key: string; value: unknown; source: string; origin: 'file' | 'answer' | 'default' }
export type AnswerDiff = {
  added: { key: string; value: unknown; source: string }[]
  tightened: { key: string; from: unknown; to: unknown; source: string }[]
  unchanged: { key: string; value: unknown }[]
  /** In the current version, not in the new one. */
  removed: { key: string; value: unknown }[]
  /** A limit raised against the current version. */
  loosened: { key: string; from: unknown; to: unknown; source: string }[]
}
export type AnswersPreview = {
  agent: string
  current_version: number | null
  next_version: number
  source: string
  compiled: Record<string, unknown>
  compiled_hash: string
  lineage: AnswerLineage[]
  unanswered: string[]
  diff: AnswerDiff
  /** null when the request carried no canon. */
  canon: { canon_birth_id: string | null; reused: boolean; placement: { rn_id: string; status: 'identity-bearing' | 'QUARANTINE-REVIEW'; coords: (number | string)[] } } | null
  no_change: boolean
}
export type RebirthResult = { notebook_id: string; notebook_version: number; birth_cert_id: string; compiled_hash: string }
export type NotebookVersion = {
  notebook_version: number
  birth_cert_id: string
  supersedes_birth_cert_id: string | null
  compiled_hash: string
  source: 'import' | 'answers' | 'template' | 'import+answers'
  signed_by: { user_id: string; name: string | null }
  signed_at: number
  answers: Record<string, unknown> | null
  canon_birth_id: string | null
  current: boolean
}
export type NotebookVersions = { notebook_id: string; items: NotebookVersion[] }

const agentPath = (projectId: string, agent: string) => `/projects/${projectId}/agents/${encodeURIComponent(agent)}`
export const answersPreviewPath = (projectId: string, agent: string) => `${agentPath(projectId, agent)}/answers/preview`
export const rebirthPath = (projectId: string, agent: string) => `${agentPath(projectId, agent)}/rebirth`
/** The Notebook's versions; NOT `…/versions`, which is the agent-file route. */
export const notebookVersionsPath = (projectId: string, agent: string) => `${agentPath(projectId, agent)}/notebook/versions`

/** Plain label for a Notebook version's source. */
export function versionSourceLabel(source: NotebookVersion['source']): string {
  if (source === 'import') return 'imported from the agent file'
  if (source === 'import+answers') return 'the agent file plus your answers'
  if (source === 'answers') return 'your answers'
  return 'a template'
}

export const BIRTH_ALL_NOTE = 'Each agent gets a signed Notebook now. Its intake answers and rules come later.'

export type ImportResult = { project_id: string; created: boolean; snapshot_id: string; agents_changed: string[] }
export type ProjectPage<T> = Page<T>

/** §6s: reads any tenant user; import, refine, notebook and top-level agent team-admin+. §6t: publish security-admin+. */
export const canEditProjects = (role: string | undefined) => atLeast(role, 'team-admin')
export const canPublishProject = (role: string | undefined) => atLeast(role, 'security-admin')

/** Human text for the §6s/§6t refusals the pages can hit; `null` when the code has no special wording. */
export function projectErrorText(e: { code: string; message: string; details?: unknown }): string {
  const d = (e.details ?? {}) as { reason?: string; path?: string; code?: string }
  const sub = d.code ?? d.reason
  if (e.code === 'assumed_read_only') return 'This assumed session is read-only.'
  if (e.code === 'too_large') return 'That file is over 4 MiB, the most a snapshot may be.'
  if (e.code === 'validation_error' && d.reason) return `Rejected (${d.reason}${d.path ? ` at ${d.path}` : ''}). Nothing was stored.`
  if (sub === 'notebook_exists') return 'This agent already has a Notebook.'
  if (sub === 'no_change') return 'Nothing changed: this would be identical to the current version, so nothing was saved.'
  if (sub === 'nothing_to_release') return 'This project has no agents to release.'
  if (e.code === 'kek_unavailable') return 'The tenant signing key is not configured. Nothing was stored.'
  return e.message
}

export type DiffLine = { kind: 'same' | 'add' | 'del'; text: string }

/** Line diff (longest common subsequence) of `before` to `after`, for showing a refinement against the scanned version. */
export function lineDiff(before: string, after: string): DiffLine[] {
  const a = before.split('\n')
  const b = after.split('\n')
  const dp: number[][] = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0))
  for (let i = a.length - 1; i >= 0; i--)
    for (let j = b.length - 1; j >= 0; j--) dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1])
  const out: DiffLine[] = []
  let i = 0
  let j = 0
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) { out.push({ kind: 'same', text: a[i] }); i++; j++ }
    else if (dp[i + 1][j] >= dp[i][j + 1]) out.push({ kind: 'del', text: a[i++] })
    else out.push({ kind: 'add', text: b[j++] })
  }
  while (i < a.length) out.push({ kind: 'del', text: a[i++] })
  while (j < b.length) out.push({ kind: 'add', text: b[j++] })
  return out
}

/** The agent file as the Mac will see it: a frontmatter block then the body (diff display only; §6t `content` is the server's). */
export const agentText = (frontmatter: Record<string, unknown>, body: string) => `${JSON.stringify(frontmatter, null, 2)}\n---\n${body}`

/** Model and tools from an agent's frontmatter, for the list. */
export function agentSummary(fm: Record<string, unknown>): { model: string; tools: string } {
  const tools = fm.tools
  return {
    model: typeof fm.model === 'string' ? fm.model : '—',
    tools: Array.isArray(tools) ? tools.join(', ') : typeof tools === 'string' ? tools : '—',
  }
}
